import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  LegendComponent,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import Loading from "../../../components/Loading";
import { renderHourTick } from "../../../components/recharts/components";
import { BrushStart } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { R_CHART_TYPES } from "../../Rajasthan/const";
import { TradersData } from "../../../models/commonTraders";
import { COLOR_CODES } from "../../../common";
import { SearchBox } from "../../../components/Search";

const UNITS = ["MW", "MU"];

const VIEW_MODES = ["Cumulative", "Comparative"];

export enum ChartType {
  Line,
  Bar,
  Area,
}

const monthToNumber = (monthStr: string) => {
  const months = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };
  const monthPart = monthStr.split(" ")[0] as keyof typeof months;
  return months[monthPart] || 0;
};

const Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

interface ChartData {
  blockNumber: number;
  date: string;
  purchase_price: number;
  sales_price: number;
  total_volume: number;
  [key: string]: any;
}

interface ChartTraderProps {
  data: TradersData[];
  fetchTraderData: any;
  startDate: string;
  endDate: string;
  Intervals: any;
}

export function ChartTraderComponent({
  data,
  fetchTraderData,
  startDate,
  endDate,
  Intervals,
}: ChartTraderProps) {
  const [selectedUnit, setSelectedUnit] = useState(UNITS[1]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [loading, setLoading] = useState(true);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState(VIEW_MODES[0]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setLoading(true);
      return;
    }

    const groupedData: { [key: string]: any } = {};
    const uniqueTraders = new Set<string>();
    const uniqueValues = new Set();

    data.forEach((entry) => {
      uniqueValues.add(entry.time_block);
    });

    setUniqueBlock(Array.from(uniqueValues));

    const filteredData =
      selectedBlock.length > 0
        ? data.filter((item) =>
            selectedBlock.map((block) => block.value).includes(item?.time_block)
          )
        : data;
    console.log("filter", filteredData);

    filteredData.forEach((entry) => {
      const { date: entryDate, month, time_block: blockNumber } = entry;
      if (!entryDate || entryDate < startDate || entryDate > endDate) return;

      const formattedDate = entryDate.split("T")[0];
      const trader = entry.trader || "Unknown Trader";
      const volume = entry.edited_scheduled_volume_mus
        ? parseFloat(entry.edited_scheduled_volume_mus.toFixed(2))
        : 0;

      const groupKey =
        Intervals?.key === "time"
          ? `${formattedDate}-${blockNumber}`
          : Intervals.key === "date"
          ? formattedDate
          : month;

      if (groupKey && !groupedData[groupKey]) {
        groupedData[groupKey] = {
          date: formattedDate,
          month,
          blockNumber: ["time", "date", "month"].includes(Intervals.key)
            ? blockNumber
            : null,
          total_volume: 0,
          total_purchase_price: 0,
          total_sales_price: 0,
          monthIndex:
            Intervals.key === "month" && month ? monthToNumber(month) : 0,
          yearValue:
            month && month.split(" ").length > 1
              ? parseInt(month.split(" ")[1])
              : 0,
        };
      }

      if (groupKey) {
        groupedData[groupKey][trader] =
          (groupedData[groupKey][trader] || 0) + volume;
        groupedData[groupKey].total_volume += volume;
        groupedData[groupKey][`${trader}_purchase_price`] =
          (groupedData[groupKey][`${trader}_purchase_price`] || 0) +
          (entry.edited_purchase_price_rs_per_kwh || 0);
        groupedData[groupKey][`${trader}_sales_price`] =
          (groupedData[groupKey][`${trader}_sales_price`] || 0) +
          (entry.edited_sale_price_transaction_price_rs_per_kwh || 0);
        groupedData[groupKey].total_purchase_price +=
          entry.edited_purchase_price_rs_per_kwh || 0;
        groupedData[groupKey].total_sales_price +=
          entry.edited_sale_price_transaction_price_rs_per_kwh || 0;
      }

      uniqueTraders.add(trader);
    });

    if (Object.keys(groupedData).length === 0) {
      setChartData([]);
      setChartKeys([]);
      setLoading(false);
      return;
    }

    Object.values(groupedData).forEach((entry: any) => {
      uniqueTraders.forEach((trader) => {
        entry[trader] = entry[trader] || 0;
        entry[`${trader}_purchase_price`] =
          entry[`${trader}_purchase_price`] || 0;
        entry[`${trader}_sales_price`] = entry[`${trader}_sales_price`] || 0;
      });
    });

    if (viewMode === "Cumulative") {
      let sortedData: ChartData[] = Object.values(groupedData).sort(
        (a: ChartData, b: ChartData) => {
          if (Intervals.key === "month") {
            return a.yearValue !== b.yearValue
              ? a.yearValue - b.yearValue
              : a.monthIndex - b.monthIndex;
          }
          return a.date === b.date
            ? a.blockNumber - b.blockNumber
            : a.date.localeCompare(b.date);
        }
      );

      if (selectedBlock.length > 1) {
        const aggregatedData: { [key: string]: any } = {};

        sortedData.forEach((entry) => {
          const aggKey =
            Intervals.key === "time" ? entry.date : entry[Intervals.key];

          if (!aggregatedData[aggKey]) {
            aggregatedData[aggKey] = {
              ...entry,
              total_volume: 0,
              total_purchase_price: 0,
              total_sales_price: 0,
            };
            uniqueTraders.forEach((trader) => {
              aggregatedData[aggKey][trader] = 0;
              aggregatedData[aggKey][`${trader}_purchase_price`] = 0;
              aggregatedData[aggKey][`${trader}_sales_price`] = 0;
            });
          }

          aggregatedData[aggKey].total_volume += entry.total_volume;
          aggregatedData[aggKey].total_purchase_price +=
            entry.total_purchase_price;
          aggregatedData[aggKey].total_sales_price += entry.total_sales_price;

          uniqueTraders.forEach((trader) => {
            aggregatedData[aggKey][trader] += entry[trader] || 0;
            aggregatedData[aggKey][`${trader}_purchase_price`] +=
              entry[`${trader}_purchase_price`] || 0;
            aggregatedData[aggKey][`${trader}_sales_price`] +=
              entry[`${trader}_sales_price`] || 0;
          });
        });

        sortedData = Object.values(aggregatedData);
      }

      setChartData(sortedData);
    }
    setChartKeys(Array.from(uniqueTraders));
    setLoading(false);
  }, [data, startDate, endDate, selectedBlock, Intervals, viewMode]);

  // const xDataKey = useMemo(
  //   () => (Intervals?.key === "time" ? "blockNumber" : Intervals?.key),
  //   [Intervals?.key]
  // );

  const handlexDataKey = useCallback(() => {
    if (Intervals.key === "time") {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "blockNumber";
    } else {
      return Intervals.key;
    }
    // if (Intervals.key === "time") {
    //   return "blockNumber";
    // }
  }, [selectedBlock, Intervals.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const getTraderLegends = () => {
    const legends = chartKeys.flatMap((trader, index) => [
      {
        dataKey: `${trader}_purchase_price`,
        name: `${trader} Purchase Price (₹)`,
        stroke: ExchangeColors[index % ExchangeColors.length],
        type: "line" as unknown as ChartType,
        yAxisId: "right",
        fill: "none",
        stackId: selectedChartType.stackId ? `${trader}_prices` : undefined,
      },
      {
        dataKey: `${trader}_sales_price`,
        name: `${trader} Sales Price (₹)`,
        stroke: ExchangeColors[(index + 1) % ExchangeColors.length],
        type: "line" as unknown as ChartType,
        yAxisId: "right",
        fill: "none",
        stackId: selectedChartType.stackId ? `${trader}_prices` : undefined,
      },
    ]);
    return legends;
  };

  const getLegends = () => [
    ...chartKeys.map((key, index) => ({
      dataKey: key,
      name: key.replace("_scheduled_volume_mwh", "").replace(/_/g, " "),
      stroke: ExchangeColors[index % ExchangeColors.length],
      type: 1,
      stackId: selectedChartType.stackId ? key : undefined,
    })),
    ...getTraderLegends(),
  ];

  return (
    <div>
      <div className="flex w-full gap-2 bg-gray-100">
        <div className="flex-1 flex flex-col bg-white p-2 rounded-lg shadow-md m-4">
          <div className="flex flex-col mb-4 w-full justify-end">
            <div
              id="chart_trader_heading "
              className="flex justify-between items-center w-full border-b pb-3"
            >
              <h2 id="chart_trader_Overview" className="text-left w-60">
                Overview
              </h2>
              {/* <select
                className="border px-3 py-1 rounded-md"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                {VIEW_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select> */}
              <SearchBox
                options={uniqueBlock.map((item) => ({
                  value: item,
                  label: item,
                }))}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />
            </div>

            <div
              id="trader-schedule-head"
              className="w-full pt-4 text-sm text-center "
            >
              Scheduled Volume & Prices
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-x-8 md:gap-x-4 sm:gap-x-2 mb-4">
            <div className="flex items-center space-x-2">
              {LegendComponent(
                {
                  dataKey: "edited_scheduled_volume_mus",
                  stroke: COLOR_CODES.secondary,
                  name: "Volume(MUs)",
                  type: 1,
                  legendColor: COLOR_CODES.secondary,
                },
                0,
                [],
                () => {}
              )}
            </div>
            <div className="flex items-center space-x-2">
              {LegendComponent(
                {
                  dataKey: "edited_sale_price_transaction_price_rs_per_kwh",
                  stroke: COLOR_CODES.secondary,
                  name: "Sales Price (₹/kwh)",
                  yAxisId: "right",
                },
                1,
                [],
                () => {}
              )}
            </div>
            <div className="flex items-center space-x-2">
              {LegendComponent(
                {
                  dataKey: "edited_purchase_price_rs_per_kwh",
                  stroke: COLOR_CODES.secondary,
                  name: "Purchase Price (₹/kwh)",
                  yAxisId: "right",
                },
                1,
                [],
                () => {}
              )}
            </div>
          </div>
          <div
            id="traderChart"
            className="w-full h-[100vh] sm:h-[60vh] xs:h-[40vh] bg-white rounded-md shadow-sm overflow-x-auto"
          >
            {loading ? (
              chartData.length === 0 ? (
                <ReMixChart
                  data={[]}
                  showBrush={false}
                  unit={""}
                  yAxisLabel={selectedUnit}
                  legends={[]}
                  xDataKey={xDataKey}
                  xTick={(value: any) => renderHourTick(value, 10)}
                  secondYAxisLabel="Price(Rs/KWh)"
                  secondXDataKey="date"
                />
              ) : (
                <Loading />
              )
            ) : (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96}
                brushStart={BrushStart.Start}
                unit={""}
                yAxisLabel={selectedUnit}
                legends={getLegends()}
                isTimeSlot={selectedBlock.length < 1 && Intervals.isTime}
                // xDataKey={selectedBlock.length > 0 ? "blockNumber" : xDataKey}
                // xDataKey="blockNumber"
                xDataKey={xDataKey}
                xTick={
                  xDataKey === "blockNumber"
                    ? (value: any) => renderHourTick(value, 10)
                    : undefined
                }
                secondYAxisLabel="Price(Rs/KWh)"
                secondXDataKey={
                  Intervals?.isTime && selectedBlock.length === 0
                    ? Intervals?.values[1]
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
